import { createContext, useContext } from "react";

import {
	useGetTicketAttachmentBlobQuery,
	useLazyGetTicketAttachmentBlobQuery,
} from "@/pages/Private/redux/tickets/tickets.api";

export interface TicketAttachmentBlobArg {
	ticketId: number;
	attachmentId: number;
}

/**
 * Where an attachment's bytes come from. The chip and the thumbnail hook are shared between the
 * customer detail (`tickets/:id/attachments/…`) and the staff detail (`tickets/admin/…`, PREC-3940
 * decision 10): the page that knows which routes it may call provides its pair of RTK hooks, the
 * components never decide. Both members are React hooks — call them unconditionally, at the top.
 */
export interface TicketAttachmentSource {
	useBlobQuery: (arg: TicketAttachmentBlobArg, options: { skip: boolean }) => { data?: Blob };
	useLazyBlobQuery: () => readonly [
		(arg: TicketAttachmentBlobArg) => { unwrap: () => Promise<Blob> },
		...unknown[],
	];
}

/** The default: the customer routes, so every existing customer call site is unchanged. */
export const CUSTOMER_ATTACHMENT_SOURCE: TicketAttachmentSource = {
	useBlobQuery: useGetTicketAttachmentBlobQuery,
	useLazyBlobQuery: useLazyGetTicketAttachmentBlobQuery,
};

export const TicketAttachmentSourceContext = createContext<TicketAttachmentSource>(
	CUSTOMER_ATTACHMENT_SOURCE
);

export const useTicketAttachmentSource = (): TicketAttachmentSource =>
	useContext(TicketAttachmentSourceContext);
